import { z } from 'zod';
import type { Stage } from '../enums';
import { assertTypes, type NoNewFields } from '../primitives';
import { type BrandKey, zBrandKey } from '../restaurantPlans/restaurantPlanSchema';

/**
 * Each brand's site domain per stage: its venues' sites are `<subdomain>.<site domain>`, served by
 * the stage's consumer-web project's wildcard domain (`*.<site domain>`). A brand without one gives
 * its venues no site.
 * - `dev` covers local, preview, pods and e2e: `*.localhost` resolves to this machine.
 */
export const SITE_DOMAINS_BY_STAGE = {
  dev: { zest: 'zestordering.localhost', tripleshot: 'tripleshot.localhost' },
  staging: { zest: 'sta.zestordering.site', tripleshot: 'sta.tripleshot.site' },
  production: { zest: 'zestordering.site', tripleshot: 'tripleshot.site' },
} satisfies Record<Stage, Partial<Record<BrandKey, string>>>;

/**
 * Names never given out as a site name (`<subdomain>.<brand's site domain>`):
 * infrastructure, the mailboxes certificate authorities validate domains with, app routes,
 * environments, our brands, and payment and delivery brands.
 */
export const RESERVED_SUBDOMAINS: readonly string[] = [
  // Infrastructure
  'www',
  'mail',
  'smtp',
  'mx',
  'ns1',
  'ns2',
  'ns3',
  'ns4',
  'ns5',
  'ns6',
  'ns7',
  'ns8',
  'ns9',
  'ftp',
  'autoconfig',
  'autodiscover',
  'wpad',
  // Certificate authority validation mailboxes
  'admin',
  'administrator',
  'hostmaster',
  'postmaster',
  'webmaster',
  'root',
  'abuse',
  'security',
  // App routes
  'app',
  'api',
  'dashboard',
  'login',
  'auth',
  'account',
  'help',
  'support',
  'status',
  'docs',
  'billing',
  'cdn',
  'static',
  'order',
  'orders',
  'signup',
  'pos',
  // Environments
  'dev',
  'sta',
  'staging',
  'preview',
  'test',
  'demo',
  // Our brands
  'zest',
  'zestordering',
  'orderhub',
  'tripleshot',
  // Payment and delivery brands
  'paypal',
  'stripe',
  'square',
  'apple',
  'google',
  'ubereats',
  'doordash',
  'menulog',
  'deliveroo',
];

/**
 * A site name, e.g. `mycafe` in the Zest site `mycafe.zestordering.site`.
 * - 3–40 lowercase letters, numbers and single hyphens, never at the start or end. No `--` also
 *   rules out `xn--` lookalike (punycode) names.
 * - Never one of `RESERVED_SUBDOMAINS`.
 */
export const zSubdomain = z
  .string()
  .min(3, 'Use at least 3 characters')
  .max(40, 'Use 40 characters or fewer')
  .regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    'Use letters, numbers and single hyphens, with no hyphen at the start or end',
  )
  .refine((subdomain) => !RESERVED_SUBDOMAINS.includes(subdomain), {
    message: "That address isn't available. Try another one.",
  });

/**
 * A venue's site: its brand's free online ordering site at `<subdomain>.<brand's site domain>`.
 * - One row per venue with a site. No row: the venue's brand has no sites.
 * - `brand` is always the venue's brand (`syncWithBrand`), and names are unique per brand.
 * - The name is the same in every stage.
 */
export const siteSchema = z.object({
  restaurantId: z.string(),
  /** The brand whose site domain serves the site. */
  brand: zBrandKey,
  /** `zSubdomain`, e.g. `mycafe` */
  subdomain: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type Site = z.output<typeof siteSchema>;

/** Strict schema */
export const siteStrictSchema = siteSchema.safeExtend({
  subdomain: zSubdomain,
});

/** Convex schema */
export const siteCvxSchema = siteSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type SiteCvx = z.output<typeof siteCvxSchema>;

assertTypes<NoNewFields<Site, SiteCvx>>();
